import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import useActionSyncUser from "../../../../../../../hooks/useSyncUser";
import { Measurement } from "../../../../../../../models/measurement";
import { Searches, Filters, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import MeasurementService from "../../../../../../../services/measurement.service";

const useActions = () => {
  const { currentUser } = useAppState();

  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();
  const { mutate: syncUser } = useActionSyncUser();

  const handleAsyncUser = (measurementId: number) => {
    if (measurementId === currentUser?.measurementId) {
      syncUser();
    }
  };

  const { refetch, isFetching } = useQuery(
    ["measurement.list", page, pageSize, filters, searches, sorting],
    () =>
      MeasurementService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify([...sorting]) : JSON.stringify([["description", "asc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setMeasurements(response.data);
      },
    }
  );

  const { refetch: getMeasurementCount } = useQuery(
    ["measurement.count", filters, searches],
    () =>
      MeasurementService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createMeasurement, isLoading: isCreating } = useMutation(
    ["measurement.create"],
    (measurement: Measurement) => MeasurementService.create(measurement),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getMeasurementCount();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateMeasurement, isLoading: isUpdating } = useMutation(
    ["measurement.update"],
    ({ id, measurement }: { id: number; measurement: Measurement }) =>
      MeasurementService.update(id, measurement),
    {
      onSuccess: (_, body) => {
        setHasErrors(false);
        refetch();
        getMeasurementCount();
        handleAsyncUser(body?.id);
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    measurements,
    page,
    pageSize,
    total,
    hasErrors,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    isLoading: isFetching || isCreating || isUpdating,
    handleSearch,
    handleFilter,
    handleSort,
    updateMeasurement,
    createMeasurement,
  };
};

export default useActions;
